import { useState, useRef } from 'react';
import { Pencil, Check, X, Trash2, GripVertical, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CurriculumModule, CurriculumTopic } from '@/lib/api/curriculum';

type Tab = 'topics' | 'settings';

const typeColors: Record<string, string> = {
  lecture: 'bg-blue-50 text-blue-700',
  lab: 'bg-amber-50 text-amber-700',
  project: 'bg-purple-50 text-purple-700',
};

const phaseColors: Record<string, string> = {
  fundamentals: 'bg-slate-100 text-slate-700',
  frontend: 'bg-cyan-50 text-cyan-700',
  backend: 'bg-green-50 text-green-700',
  fullstack: 'bg-violet-50 text-violet-700',
};

interface ModuleDetailProps {
  module: CurriculumModule;
  onUpdateModule: (id: string, data: Record<string, unknown>) => void;
  onDeleteModule: (id: string) => void;
  onUpdateTopic: (id: string, data: {
    title?: string; estimated_hours?: number; topic_type?: string; sort_order?: number;
  }) => void;
  onDeleteTopic: (id: string) => void;
  onAddTopic?: () => void;
  onTopicClick?: (topicId: string) => void;
  selectedTopicId?: string;
}

export function ModuleDetail({
  module, onUpdateModule, onDeleteModule,
  onUpdateTopic, onDeleteTopic,
  onAddTopic, onTopicClick, selectedTopicId,
}: ModuleDetailProps) {
  const [tab, setTab] = useState<Tab>('topics');

  // Settings form state
  const [settingsTitle, setSettingsTitle] = useState(module.title);
  const [settingsPhase, setSettingsPhase] = useState(module.phase);
  const [settingsDays, setSettingsDays] = useState(module.estimated_days?.toString() ?? '');
  const [settingsDesc, setSettingsDesc] = useState(module.description ?? '');
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [deleteModuleConfirm, setDeleteModuleConfirm] = useState(false);

  // Topic editing
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [topicEdit, setTopicEdit] = useState({ title: '', topic_type: 'lecture', estimated_hours: '' });
  const [deleteTopicId, setDeleteTopicId] = useState<string | null>(null);

  // Drag-and-drop
  const dragIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const topics = [...(module.topics ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  const handleEditTopic = (topic: CurriculumTopic) => {
    setEditingTopicId(topic.id);
    setTopicEdit({
      title: topic.title,
      topic_type: topic.topic_type,
      estimated_hours: topic.estimated_hours?.toString() ?? '',
    });
  };

  const handleSaveTopic = () => {
    if (!editingTopicId || !topicEdit.title.trim()) return;
    onUpdateTopic(editingTopicId, {
      title: topicEdit.title.trim(),
      topic_type: topicEdit.topic_type,
      estimated_hours: topicEdit.estimated_hours ? parseFloat(topicEdit.estimated_hours) : undefined,
    });
    setEditingTopicId(null);
  };

  const handleDrop = (dropIdx: number) => {
    const fromIdx = dragIdx.current;
    setDragOverIdx(null);
    dragIdx.current = null;
    if (fromIdx === null || fromIdx === dropIdx) return;
    const reordered = [...topics];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    reordered.forEach((t, i) => {
      if (t.sort_order !== i + 1) onUpdateTopic(t.id, { sort_order: i + 1 });
    });
  };

  const handleSaveSettings = () => {
    if (!settingsTitle.trim()) return;
    onUpdateModule(module.id, {
      title: settingsTitle.trim(),
      phase: settingsPhase,
      estimated_days: settingsDays ? parseInt(settingsDays, 10) : undefined,
      description: settingsDesc.trim() || undefined,
    });
    setSettingsDirty(false);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Module header */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-6 py-4">
        <h2 className="flex-1 text-base font-semibold">{module.title}</h2>
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', phaseColors[module.phase])}>
          {module.phase}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex flex-shrink-0 border-b border-border px-6">
        {(['topics', 'settings'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'mr-6 border-b-2 py-3 text-sm font-medium capitalize transition-colors',
              tab === t
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'topics' && (
          <div className="p-6">
            {topics.length > 0 ? (
              <ul className="mb-4 space-y-1">
                {topics.map((topic, idx) => (
                  <li
                    key={topic.id}
                    draggable={editingTopicId !== topic.id && deleteTopicId !== topic.id}
                    onDragStart={() => { dragIdx.current = idx; }}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                    onDragLeave={() => setDragOverIdx(null)}
                    onDrop={() => handleDrop(idx)}
                    className={cn(
                      'flex items-center gap-2 rounded px-2 py-1.5 text-sm',
                      dragOverIdx === idx && dragIdx.current !== idx
                        ? 'bg-primary/10 outline outline-1 outline-primary/50'
                        : selectedTopicId === topic.id
                          ? 'bg-primary/5 outline outline-1 outline-primary/30'
                          : 'hover:bg-muted/50',
                      editingTopicId !== topic.id && deleteTopicId !== topic.id && 'cursor-pointer',
                    )}
                    onClick={(e) => {
                      if (editingTopicId === topic.id || deleteTopicId === topic.id) return;
                      // Don't trigger if click came from interactive child (button/input/select)
                      const target = e.target as HTMLElement;
                      if (target.closest('button, input, select, textarea')) return;
                      onTopicClick?.(topic.id);
                    }}
                  >
                    {editingTopicId === topic.id ? (
                      <div className="flex flex-1 items-center gap-2">
                        <input
                          value={topicEdit.title}
                          onChange={(e) => setTopicEdit({ ...topicEdit, title: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTopic();
                            if (e.key === 'Escape') setEditingTopicId(null);
                          }}
                          className="flex-1 rounded border border-border bg-background px-2 py-0.5 text-xs"
                          autoFocus
                        />
                        <select
                          value={topicEdit.topic_type}
                          onChange={(e) => setTopicEdit({ ...topicEdit, topic_type: e.target.value })}
                          className="rounded border border-border bg-background px-2 py-0.5 text-xs"
                        >
                          <option value="lecture">Lecture</option>
                          <option value="lab">Lab</option>
                          <option value="project">Project</option>
                        </select>
                        <input
                          placeholder="hrs"
                          value={topicEdit.estimated_hours}
                          onChange={(e) => setTopicEdit({ ...topicEdit, estimated_hours: e.target.value })}
                          className="w-14 rounded border border-border bg-background px-2 py-0.5 text-xs"
                          type="number"
                          step="0.25"
                        />
                        <button onClick={handleSaveTopic} className="text-green-600" title="Save topic">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setEditingTopicId(null)} className="text-muted-foreground">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : deleteTopicId === topic.id ? (
                      <div className="flex flex-1 items-center gap-2 text-xs">
                        <span className="flex-1 font-medium text-destructive">Delete "{topic.title}"?</span>
                        <button
                          onClick={() => { onDeleteTopic(topic.id); setDeleteTopicId(null); }}
                          className="rounded bg-destructive px-2 py-0.5 text-white"
                        >Yes</button>
                        <button
                          onClick={() => setDeleteTopicId(null)}
                          className="rounded border border-border px-2 py-0.5 hover:bg-muted"
                        >No</button>
                      </div>
                    ) : (
                      <>
                        <GripVertical className="h-3 w-3 flex-shrink-0 text-muted-foreground/40" />
                        <span className="w-6 text-right text-xs text-muted-foreground">{topic.sort_order}.</span>
                        <span className="flex-1">{topic.title}</span>
                        <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium', typeColors[topic.topic_type])}>
                          {topic.topic_type}
                        </span>
                        {topic.estimated_hours != null && (
                          <span className="text-xs text-muted-foreground">{topic.estimated_hours}h</span>
                        )}
                        <button
                          onClick={() => handleEditTopic(topic)}
                          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                          title="Edit topic"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setDeleteTopicId(topic.id)}
                          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                          title="Delete topic"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-4 text-sm text-muted-foreground">No topics yet.</p>
            )}
            <button
              type="button"
              onClick={onAddTopic}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add Topic
            </button>
          </div>
        )}

        {tab === 'settings' && (
          <div className="max-w-md space-y-4 p-6">
            <div>
              <label className="mb-1 block text-sm font-medium">Title</label>
              <input
                value={settingsTitle}
                onChange={(e) => { setSettingsTitle(e.target.value); setSettingsDirty(true); }}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Phase</label>
              <input
                value={settingsPhase}
                onChange={(e) => { setSettingsPhase(e.target.value); setSettingsDirty(true); }}
                placeholder="e.g. Backend, Devops, AI"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Estimated Days</label>
              <input
                type="number"
                min="1"
                value={settingsDays}
                onChange={(e) => { setSettingsDays(e.target.value); setSettingsDirty(true); }}
                placeholder="Optional"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Description</label>
              <textarea
                value={settingsDesc}
                onChange={(e) => { setSettingsDesc(e.target.value); setSettingsDirty(true); }}
                placeholder="Optional module description"
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            {settingsDirty && (
              <button
                type="button"
                onClick={handleSaveSettings}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                Save Changes
              </button>
            )}
            <div className="mt-8 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <p className="mb-2 text-sm font-medium text-destructive">Danger Zone</p>
              <p className="mb-3 text-xs text-muted-foreground">
                Deleting this module removes all {topics.length} topics. This cannot be undone.
              </p>
              {deleteModuleConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-destructive">Are you sure?</span>
                  <button
                    type="button"
                    onClick={() => onDeleteModule(module.id)}
                    className="rounded bg-destructive px-3 py-1 text-xs text-white"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteModuleConfirm(false)}
                    className="rounded border border-border px-3 py-1 text-xs"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteModuleConfirm(true)}
                  className="rounded-md border border-destructive/50 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                >
                  Delete Module
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
